import { useState, useEffect } from 'react'
import { useChatStore } from '../../stores/chatStore'
import { useUiStore } from '../../stores/uiStore'
import { useAuthStore } from '../../stores/authStore'
import { mediaApi } from '../../api/media'
import { groupsApi } from '../../api/groups'
import { chatsApi } from '../../api/chats'
import { usersApi } from '../../api/users'
import { moderationApi } from '../../api/moderation'
import { callsApi, type CallRecord } from '../../api/calls'
import type { GroupMember } from '../../api/groups'
import { chatTitle, userName } from '../../lib/displayName'
import { Avatar } from '../ui/Avatar'
import { Select } from '../ui/Select'

interface Props {
  chatId: string
  onClose: () => void
}

export function ChatInfoPanel({ chatId, onClose }: Props) {
  const chat = useChatStore((s) => s.chats.find((c) => c.id === chatId))
  const messages = useChatStore((s) => s.messages[chatId] ?? [])
  const { addToast, chatWallpapers, setChatWallpaper, clearChatWallpaper } = useUiStore()
  const { userId } = useAuthStore()

  const [members, setMembers] = useState<GroupMember[]>([])
  const [inviteUrl, setInviteUrl] = useState<string | null>(null)
  const [editingGroup, setEditingGroup] = useState(false)
  const [groupName, setGroupName] = useState('')
  const [groupDesc, setGroupDesc] = useState('')
  const [slowModeSecs, setSlowModeSecs] = useState(0)
  const [mediaTab, setMediaTab] = useState<'media' | 'files' | 'links' | 'calls'>('media')
  const [callHistory, setCallHistory] = useState<CallRecord[]>([])
  const [addMemberQuery, setAddMemberQuery] = useState('')
  const [addMemberResults, setAddMemberResults] = useState<{ id: string; name: string; username?: string }[]>([])
  const [showAddMember, setShowAddMember] = useState(false)
  const [wordFilters, setWordFilters] = useState<string[]>([])
  const [filterInput, setFilterInput] = useState('')
  const [showFilters, setShowFilters] = useState(false)
  const [antiSpam, setAntiSpam] = useState(false)
  const [inviteLinks, setInviteLinks] = useState<Array<{ code: string; url: string; created_at: string }>>([])
  const [showInviteLinks, setShowInviteLinks] = useState(false)

  const mediaMessages = messages.filter((m) => m.media_ids?.length)

  const name = chatTitle(chat)

  const URL_RE_LINKS = /https?:\/\/[^\s<>"']+/g
  const links = messages.flatMap((m) =>
    m.text ? Array.from(m.text.matchAll(URL_RE_LINKS), (match) => match[0]) : []
  )

  useEffect(() => {
    setGroupName(chat?.title ?? (chat?.peer ? userName(chat.peer, '') : ''))
    setGroupDesc(chat?.peer?.bio ?? '')
    if (chat?.type === 'group') {
      groupsApi.get(chatId)
        .then((g) => setSlowModeSecs(g.slow_mode_secs ?? 0))
        .catch(() => {})
      const isAdmin = members.some((m) => m.user_id === userId && m.role === 'admin')
      if (isAdmin) {
        moderationApi.listWordFilters(chatId)
          .then((r) => setWordFilters(r.filters ?? []))
          .catch(() => {})
        moderationApi.getGroupConfig(chatId)
          .then((c) => setAntiSpam(c.anti_spam ?? false))
          .catch(() => {})
      }
    }
  }, [chatId, chat?.title, chat?.peer?.first_name, chat?.peer?.bio, chat?.type])

  useEffect(() => {
    if (chat?.type !== 'group') return
    groupsApi.listMembers(chatId)
      .then(async (r) => {
        const res = r as { members?: GroupMember[] }
        const list = res.members ?? []
        setMembers(list)
        // Подтягиваем профили участников — listMembers отдаёт только user_id+role,
        // иначе в списке отображаются сырые ID.
        const enriched = await Promise.all(
          list.map(async (m) =>
            m.user ? m : { ...m, user: await usersApi.getProfile(m.user_id).catch(() => undefined) },
          ),
        )
        setMembers(enriched)
      })
      .catch(() => {})
  }, [chatId, chat?.type])

  const handleSaveGroup = async () => {
    try {
      await groupsApi.update(chatId, {
        name: groupName.trim() || undefined,
        description: groupDesc.trim() || undefined,
        slow_mode_secs: slowModeSecs,
      })
      useChatStore.getState().upsertChat({ ...chat!, title: groupName.trim() })
      addToast('success', 'Группа обновлена')
      setEditingGroup(false)
    } catch {
      addToast('error', 'Не удалось обновить группу')
    }
  }

  const handleCreateInvite = async () => {
    try {
      const r = await groupsApi.createInvite(chatId) as { url?: string; code?: string }
      const url = r.url ?? `https://ggram.top/join/${r.code}`
      setInviteUrl(url)
      navigator.clipboard.writeText(url)
      addToast('success', 'Ссылка скопирована!')
      // Refresh list
      groupsApi.listInvites(chatId)
        .then((res) => setInviteLinks(res.links ?? []))
        .catch(() => {})
    } catch {
      addToast('error', 'Не удалось создать ссылку')
    }
  }

  useEffect(() => {
    if (mediaTab !== 'calls') return
    callsApi.getHistory(50)
      .then((r) => setCallHistory(r.records ?? []))
      .catch(() => {})
  }, [mediaTab])

  const handleLoadInviteLinks = async () => {
    setShowInviteLinks((v) => !v)
    if (!showInviteLinks) {
      try {
        const res = await groupsApi.listInvites(chatId)
        setInviteLinks(res.links ?? [])
      } catch {
        addToast('error', 'Не удалось загрузить ссылки')
      }
    }
  }

  const handleRevokeInvite = async (code: string) => {
    try {
      await groupsApi.revokeInvite(chatId, code)
      setInviteLinks((l) => l.filter((x) => x.code !== code))
      addToast('success', 'Ссылка отозвана')
    } catch {
      addToast('error', 'Не удалось отозвать ссылку')
    }
  }

  const handleKick = async (uid: string) => {
    try {
      await groupsApi.kick(chatId, uid)
      await chatsApi.removeGroupMember(chatId, uid).catch(() => null)
      setMembers((m) => m.filter((x) => x.user_id !== uid))
      addToast('success', 'Участник исключён')
    } catch {
      addToast('error', 'Не удалось исключить участника')
    }
  }

  const handleBan = async (uid: string) => {
    try {
      await moderationApi.banUser(chatId, uid)
      setMembers((m) => m.filter((x) => x.user_id !== uid))
      addToast('success', 'Пользователь заблокирован')
    } catch {
      addToast('error', 'Не удалось заблокировать')
    }
  }

  const handleMuteMember = async (uid: string) => {
    try {
      await moderationApi.muteUser(chatId, uid, 3600)
      addToast('success', 'Пользователь замолчан на 1 час')
    } catch {
      addToast('error', 'Не удалось замолчать пользователя')
    }
  }

  const handleToggleAdmin = async (uid: string, currentRole: string) => {
    const newRole = currentRole === 'admin' ? 'member' : 'admin'
    try {
      await groupsApi.updateRole(chatId, uid, newRole)
      setMembers((m) => m.map((x) => x.user_id === uid ? { ...x, role: newRole } : x))
      addToast('success', newRole === 'admin' ? 'Назначен администратором' : 'Права администратора сняты')
    } catch {
      addToast('error', 'Не удалось изменить роль')
    }
  }

  const handleAddMemberSearch = async (q: string) => {
    setAddMemberQuery(q)
    if (q.length < 2) { setAddMemberResults([]); return }
    try {
      const res = await usersApi.search(q)
      const existingIds = new Set(members.map((m) => m.user_id))
      setAddMemberResults(
        (res.users ?? [])
          .filter((u) => !existingIds.has(u.id) && u.id !== userId)
          .map((u) => ({
            id: u.id,
            name: [u.first_name, u.last_name].filter(Boolean).join(' ') || u.username || u.id,
            username: u.username,
          }))
      )
    } catch { setAddMemberResults([]) }
  }

  const handleAddMember = async (uid: string, name: string) => {
    try {
      await groupsApi.addMember(chatId, uid)
      await chatsApi.addGroupMember(chatId, uid).catch(() => null)
      const prof = await usersApi.getProfile(uid).catch(() => undefined)
      setMembers((m) => [...m, { user_id: uid, role: 'member', user: prof }])
      addToast('success', `${name} added to group`)
      setAddMemberQuery('')
      setAddMemberResults([])
      setShowAddMember(false)
    } catch {
      addToast('error', 'Не удалось добавить участника')
    }
  }

  const handleLeave = async () => {
    try {
      await groupsApi.leave(chatId)
      if (userId) await chatsApi.removeGroupMember(chatId, userId).catch(() => null)
      useChatStore.getState().upsertChat({ ...chat!, folder: 'archive' })
      onClose()
    } catch {
      addToast('error', 'Не удалось покинуть группу')
    }
  }

  // Действия с участниками (исключить/бан/назначить админом) доступны только админам/владельцу.
  const isAdmin = members.some(
    (m) => m.user_id === userId && (m.role === 'admin' || m.role === 'owner'),
  )

  return (
    <div className="flex flex-col w-72 shrink-0 border-l border-divider bg-bg-primary overflow-hidden animate-slide-right">
      {/* Header */}
      <div className="flex items-center gap-3 px-4 py-3 border-b border-divider shrink-0">
        <button
          onClick={onClose}
          className="text-txt-secondary hover:text-txt-primary transition-colors"
          aria-label="Close info panel"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M18 6L6 18M6 6l12 12"/>
          </svg>
        </button>
        <h3 className="text-txt-primary font-semibold text-base">Информация</h3>
      </div>

      <div className="flex-1 overflow-y-auto">
        {/* Profile section */}
        <div className="flex flex-col items-center py-6 px-4 border-b border-divider">
          <Avatar
            name={name}
            src={chat?.avatar_url ?? chat?.peer?.avatar_url}
            size={80}
            online={chat?.peer?.online}
          />
          <p className="text-txt-primary font-semibold text-lg mt-3 text-center">{name}</p>
          {chat?.peer?.username && (
            <p className="text-txt-meta text-sm">@{chat.peer.username}</p>
          )}
          {chat?.peer?.bio && (
            <p className="text-txt-secondary text-sm mt-2 text-center">{chat.peer.bio}</p>
          )}
          {chat?.peer?.online ? (
            <p className="text-accent text-xs mt-1">в сети</p>
          ) : chat?.peer?.last_seen_at ? (
            <p className="text-txt-meta text-xs mt-1">
              {(() => {
                const d = new Date(chat.peer!.last_seen_at!)
                const now = new Date()
                const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
                const yesterday = new Date(today.getTime() - 86_400_000)
                const t = d.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })
                if (now.getTime() - d.getTime() < 60_000) return 'заходил(а) только что'
                if (d >= today) return `заходил(а) в ${t}`
                if (d >= yesterday) return `заходил(а) вчера в ${t}`
                return `заходил(а) ${d.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long' })}`
              })()}
            </p>
          ) : null}

          {/* Group editing */}
          {chat?.type === 'group' && (
            editingGroup ? (
              <div className="w-full space-y-2 mt-2">
                <input value={groupName} onChange={(e) => setGroupName(e.target.value)}
                  className="w-full bg-bg-input text-txt-primary rounded-xl px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-accent"
                  placeholder="Название группы" />
                <input value={groupDesc} onChange={(e) => setGroupDesc(e.target.value)}
                  className="w-full bg-bg-input text-txt-primary rounded-xl px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-accent"
                  placeholder="Описание (необязательно)" />
                <div>
                  <label className="text-txt-meta text-xs mb-1 block">Медленный режим</label>
                  <Select
                    value={String(slowModeSecs)}
                    onChange={(v) => setSlowModeSecs(Number(v))}
                    className="w-full"
                    options={[
                      { value: '0', label: 'Выкл' },
                      { value: '10', label: '10 секунд' },
                      { value: '30', label: '30 секунд' },
                      { value: '60', label: '1 минута' },
                      { value: '300', label: '5 минут' },
                      { value: '3600', label: '1 час' },
                    ]}
                  />
                </div>
                <div className="flex gap-2">
                  <button onClick={handleSaveGroup}
                    className="flex-1 bg-accent text-white rounded-xl py-1.5 text-sm font-medium hover:opacity-90 transition-opacity">
                    Сохранить
                  </button>
                  <button onClick={() => setEditingGroup(false)}
                    className="flex-1 bg-bg-input text-txt-secondary rounded-xl py-1.5 text-sm hover:bg-bg-hover transition-colors">
                    Отмена
                  </button>
                </div>
              </div>
            ) : (
              <button onClick={() => setEditingGroup(true)}
                className="text-accent text-xs mt-1 hover:opacity-80 transition-opacity">
                Редактировать группу
              </button>
            )
          )}
        </div>

        {/* Chat wallpaper */}
        <div className="p-4 border-b border-divider">
          <p className="text-txt-secondary text-xs uppercase tracking-wider font-medium mb-2">Фон чата</p>
          <div className="flex flex-wrap gap-1.5">
            {['', '#1a1a2e', '#16213e', '#0f3460', '#1b4332', '#3d0000', '#2d1b69', 'linear-gradient(135deg,#667eea,#764ba2)', 'linear-gradient(135deg,#f093fb,#f5576c)', 'linear-gradient(135deg,#4facfe,#00f2fe)'].map((wp) => (
              <button
                key={wp || 'none'}
                onClick={() => wp ? setChatWallpaper(chatId, wp) : clearChatWallpaper(chatId)}
                className={`w-8 h-8 rounded-lg border-2 transition-all ${(chatWallpapers[chatId] ?? '') === wp ? 'border-accent scale-110' : 'border-transparent hover:border-divider'}`}
                style={wp ? { background: wp } : { background: 'transparent' }}
                title={wp || 'Default'}
              >
                {!wp && <span className="text-txt-meta text-xs flex items-center justify-center h-full">✕</span>}
              </button>
            ))}
          </div>
        </div>

        {/* Stats */}
        <div className="p-4 border-b border-divider space-y-2">
          <div className="flex justify-between text-sm">
            <span className="text-txt-secondary">Сообщения</span>
            <span className="text-txt-primary">{messages.length}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-txt-secondary">Медиафайлы</span>
            <span className="text-txt-primary">{mediaMessages.length}</span>
          </div>
          {chat?.is_pinned && (
            <div className="flex justify-between text-sm">
              <span className="text-txt-secondary">Статус</span>
              <span className="text-txt-primary">📌 Закреплён</span>
            </div>
          )}
          {chat?.is_muted && (
            <div className="flex justify-between text-sm">
              <span className="text-txt-secondary">Уведомления</span>
              <span className="text-txt-primary">🔇 Без звука</span>
            </div>
          )}
        </div>


        {/* Group members section */}
        {chat?.type === 'group' && (
          <div className="border-t border-divider">
            <div className="flex items-center justify-between px-4 py-3">
              <p className="text-txt-secondary text-xs uppercase tracking-wider font-medium">
                Участники ({members.length})
              </p>
              <div className="flex items-center gap-2">
              <button
                onClick={() => setShowAddMember((s) => !s)}
                className="text-accent text-xs hover:opacity-80 transition-opacity"
              >
                + Добавить
              </button>
              <button
                onClick={handleCreateInvite}
                className="text-accent text-xs hover:opacity-80 transition-opacity"
              >
                + Ссылка
              </button>
              </div>
            </div>
            {/* Add member search */}
            {showAddMember && (
              <div className="px-4 pb-2">
                <input
                  autoFocus
                  value={addMemberQuery}
                  onChange={(e) => handleAddMemberSearch(e.target.value)}
                  placeholder="Поиск пользователей..."
                  className="w-full bg-bg-input text-txt-primary rounded-xl px-3 py-1.5 text-sm outline-none focus:ring-1 focus:ring-accent placeholder-txt-meta mb-1"
                />
                {addMemberResults.map((u) => (
                  <button
                    key={u.id}
                    onClick={() => handleAddMember(u.id, u.name)}
                    className="w-full text-left flex items-center gap-2 px-2 py-1.5 hover:bg-bg-hover transition-colors rounded-lg"
                  >
                    <span className="text-base">👤</span>
                    <div className="min-w-0">
                      <p className="text-txt-primary text-sm truncate">{u.name}</p>
                      {u.username && <p className="text-txt-meta text-xs">@{u.username}</p>}
                    </div>
                  </button>
                ))}
              </div>
            )}
            {inviteUrl && (
              <div className="px-4 pb-2">
                <p className="text-txt-meta text-xs break-all">{inviteUrl}</p>
              </div>
            )}
            <div className="px-4 pb-2">
              <button
                onClick={handleLoadInviteLinks}
                className="text-accent text-xs hover:underline"
              >
                {showInviteLinks ? 'Скрыть ссылки' : 'Пригласительные ссылки'}
              </button>
              {showInviteLinks && (
                <div className="mt-2 space-y-1">
                  {inviteLinks.length === 0 && (
                    <p className="text-txt-meta text-xs">Нет активных ссылок</p>
                  )}
                  {inviteLinks.map((link) => (
                    <div key={link.code} className="flex items-center gap-2 bg-bg-input rounded-lg px-2 py-1.5">
                      <p className="text-txt-primary text-xs flex-1 truncate">{link.url ?? `…/${link.code}`}</p>
                      <button
                        onClick={() => { navigator.clipboard.writeText(link.url ?? link.code); addToast('success', 'Скопировано!') }}
                        className="text-txt-meta hover:text-accent text-xs shrink-0"
                        title="Копировать"
                      >
                        📋
                      </button>
                      <button
                        onClick={() => handleRevokeInvite(link.code)}
                        className="text-red-400 hover:text-red-300 text-xs shrink-0"
                        title="Отозвать"
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="space-y-0.5">
              {members.map((m) => (
                <div key={m.user_id} className="flex items-center gap-3 px-4 py-2 group">
                  <Avatar
                    name={userName(m.user, m.user_id)}
                    src={m.user?.avatar_url}
                    size={36}
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-txt-primary text-sm truncate">
                      {userName(m.user, m.user_id)}
                    </p>
                    <p className="text-txt-meta text-xs">{m.role}</p>
                  </div>
                  {isAdmin && m.user_id !== userId && (
                    <div className="opacity-0 group-hover:opacity-100 flex items-center gap-1 transition-opacity">
                      <button
                        onClick={() => handleToggleAdmin(m.user_id, m.role)}
                        className={`text-xs px-1.5 py-0.5 rounded transition-colors ${m.role === 'admin' ? 'text-accent hover:text-txt-secondary' : 'text-txt-meta hover:text-accent'}`}
                        title={m.role === 'admin' ? 'Снять права' : 'Назначить админом'}
                      >
                        {m.role === 'admin' ? '★' : '☆'}
                      </button>
                      <button
                        onClick={() => handleMuteMember(m.user_id)}
                        className="text-txt-meta hover:text-yellow-400 text-xs"
                        title="Замолчать на 1 час"
                      >
                        🔇
                      </button>
                      <button
                        onClick={() => handleKick(m.user_id)}
                        className="text-orange-400 hover:text-orange-300 text-xs"
                        title="Удалить из группы"
                      >
                        Исключить
                      </button>
                      <button
                        onClick={() => handleBan(m.user_id)}
                        className="text-red-400 hover:text-red-300 text-xs"
                        title="Заблокировать"
                      >
                        Блок
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
            <div className="px-4 py-3 border-t border-divider">
              <button
                onClick={handleLeave}
                className="w-full text-left text-red-400 text-sm hover:opacity-80 transition-opacity py-1"
              >
                Покинуть группу
              </button>
            </div>
          </div>
        )}

        {/* Moderation panel (group admins only) */}
        {chat?.type === 'group' && members.some((m) => m.user_id === userId && m.role === 'admin') && (
          <div className="border-t border-divider">
            <button
              onClick={() => setShowFilters((s) => !s)}
              className="w-full flex items-center justify-between px-4 py-3 text-sm text-txt-primary hover:bg-bg-hover transition-colors"
            >
              <span>🛡️ Модерация</span>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={`transition-transform ${showFilters ? 'rotate-90' : ''}`}>
                <polyline points="9 18 15 12 9 6"/>
              </svg>
            </button>
            {showFilters && (
              <div className="px-4 pb-4 space-y-3 animate-fade-in">
                {/* Anti-spam toggle */}
                <div className="flex items-center justify-between">
                  <span className="text-txt-secondary text-sm">Анти-спам</span>
                  <button
                    onClick={async () => {
                      const newVal = !antiSpam
                      setAntiSpam(newVal)
                      await moderationApi.updateGroupConfig(chatId, { anti_spam: newVal }).catch(() => {})
                    }}
                    className={`w-11 h-6 rounded-full transition-colors relative ${antiSpam ? 'bg-accent' : 'bg-txt-meta/30'}`}
                  >
                    <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${antiSpam ? 'translate-x-5' : 'translate-x-0.5'}`} />
                  </button>
                </div>

                {/* Word filters */}
                <div>
                  <p className="text-txt-meta text-xs mb-2">Фильтры слов ({wordFilters.length})</p>
                  <div className="flex gap-2 mb-2">
                    <input
                      value={filterInput}
                      onChange={(e) => setFilterInput(e.target.value)}
                      onKeyDown={async (e) => {
                        if (e.key !== 'Enter' || !filterInput.trim()) return
                        const w = filterInput.trim().toLowerCase()
                        try {
                          await moderationApi.addWordFilter(chatId, w)
                          setWordFilters((prev) => [...prev, w])
                          setFilterInput('')
                        } catch { addToast('error', 'Не удалось добавить фильтр') }
                      }}
                      placeholder="Запрещённое слово..."
                      className="flex-1 bg-bg-input text-txt-primary text-xs rounded-lg px-2 py-1.5 outline-none focus:ring-1 focus:ring-accent"
                    />
                    <button
                      onClick={async () => {
                        const w = filterInput.trim().toLowerCase()
                        if (!w) return
                        try {
                          await moderationApi.addWordFilter(chatId, w)
                          setWordFilters((prev) => [...prev, w])
                          setFilterInput('')
                        } catch { addToast('error', 'Не удалось добавить фильтр') }
                      }}
                      className="text-accent text-xs px-2 py-1.5 rounded-lg hover:bg-accent/10 transition-colors"
                    >
                      Add
                    </button>
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {wordFilters.map((w) => (
                      <button
                        key={w}
                        onClick={async () => {
                          try {
                            await moderationApi.removeWordFilter(chatId, w)
                            setWordFilters((prev) => prev.filter((x) => x !== w))
                          } catch { addToast('error', 'Не удалось удалить фильтр') }
                        }}
                        className="bg-red-400/15 text-red-400 text-xs px-2 py-0.5 rounded-lg hover:bg-red-400/25 transition-colors"
                      >
                        {w} ✕
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Media / Files / Links / Calls tabs */}
        {(mediaMessages.length > 0 || links.length > 0 || chat?.type === 'private') && (
          <div className="border-t border-divider">
            {/* Tab bar */}
            <div className="flex border-b border-divider overflow-x-auto">
              {([
                ['media', `Media (${mediaMessages.flatMap((m) => m.media_ids ?? []).length})`],
                ['files', 'Files'],
                ['links', `Links (${links.length})`],
                ...(chat?.type === 'private' ? [['calls', 'Calls']] as const : []),
              ] as [string, string][]).map(([tab, label]) => (
                <button key={tab} onClick={() => setMediaTab(tab as typeof mediaTab)}
                  className={`shrink-0 flex-1 py-2 text-xs font-medium capitalize transition-colors
                    ${mediaTab === tab ? 'text-accent border-b-2 border-accent' : 'text-txt-secondary hover:text-txt-primary'}`}>
                  {label}
                </button>
              ))}
            </div>

            {mediaTab === 'media' ? (
              <div className="grid grid-cols-3 gap-0.5 p-1">
                {mediaMessages.flatMap((m) => m.media_ids ?? []).slice(0, 18).map((id) => (
                  <MediaThumb key={id} id={id} />
                ))}
              </div>
            ) : mediaTab === 'files' ? (
              <div className="p-3 space-y-2 max-h-48 overflow-y-auto">
                {mediaMessages.flatMap((m) => m.media_ids ?? []).length > 0 ? (
                  mediaMessages.flatMap((m) => m.media_ids ?? []).slice(0, 30).map((id) => (
                    <FileEntry key={id} id={id} />
                  ))
                ) : (
                  <p className="text-txt-meta text-xs text-center py-4">No files yet</p>
                )}
              </div>
            ) : mediaTab === 'links' ? (
              <div className="p-3 space-y-2 max-h-48 overflow-y-auto">
                {links.length > 0 ? links.slice(0, 20).map((url, i) => (
                  <a key={i} href={url} target="_blank" rel="noopener noreferrer"
                     className="block text-txt-link text-xs truncate hover:opacity-80 transition-opacity">
                    {url}
                  </a>
                )) : (
                  <p className="text-txt-meta text-xs text-center py-4">No links yet</p>
                )}
              </div>
            ) : (
              <div className="p-3 space-y-1 max-h-48 overflow-y-auto">
                {callHistory.length === 0 ? (
                  <p className="text-txt-meta text-xs text-center py-4">No calls yet</p>
                ) : callHistory.map((r) => {
                  const mine = r.caller_id === userId
                  const outcomeIcon = r.outcome === 'answered' ? (mine ? '↗' : '↙') : r.outcome === 'missed' ? '📵' : '❌'
                  const durationLabel = r.duration_sec ? `${Math.floor(r.duration_sec / 60)}:${String(r.duration_sec % 60).padStart(2, '0')}` : ''
                  return (
                    <div key={r.id} className="flex items-center gap-2 px-1 py-1.5">
                      <span className="text-base shrink-0">{r.call_type === 'video' ? '📹' : '📞'}</span>
                      <div className="flex-1 min-w-0">
                        <p className="text-txt-primary text-xs">{mine ? 'Outgoing' : 'Incoming'} {r.call_type}</p>
                        <p className="text-txt-meta text-xs">{new Date(r.created_at).toLocaleDateString()}{durationLabel ? ` · ${durationLabel}` : ''}</p>
                      </div>
                      <span className={`text-sm shrink-0 ${r.outcome === 'missed' ? 'text-red-400' : 'text-txt-meta'}`}>{outcomeIcon}</span>
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

function fileIcon(mime: string): string {
  if (mime.startsWith('image/')) return '🖼️'
  if (mime.startsWith('video/')) return '🎬'
  if (mime.startsWith('audio/')) return '🎵'
  if (mime.includes('pdf')) return '📄'
  if (mime.includes('zip') || mime.includes('tar') || mime.includes('gzip')) return '🗜️'
  if (mime.includes('word') || mime.includes('document')) return '📝'
  if (mime.includes('sheet') || mime.includes('excel')) return '📊'
  return '📎'
}

function FileEntry({ id }: { id: string }) {
  const [info, setInfo] = useState<import('../../api/media').MediaFile | null>(null)
  useEffect(() => {
    mediaApi.getInfo(id).then(setInfo).catch(() => {})
  }, [id])

  return (
    <a
      href={mediaApi.getURL(id)}
      target="_blank"
      rel="noopener noreferrer"
      download
      className="flex items-center gap-2 p-2 rounded-xl bg-bg-input hover:bg-bg-hover transition-colors"
    >
      <span className="text-xl shrink-0">{info ? fileIcon(info.mime_type) : '📎'}</span>
      <div className="flex-1 min-w-0">
        <p className="text-txt-primary text-xs font-medium truncate">
          {info?.file_name ?? id.slice(0, 8) + '…'}
        </p>
        <p className="text-txt-meta text-xs">
          {info ? formatBytes(info.size) : '…'}
        </p>
      </div>
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-txt-meta shrink-0">
        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
        <polyline points="7 10 12 15 17 10"/>
        <line x1="12" y1="15" x2="12" y2="3"/>
      </svg>
    </a>
  )
}

function MediaThumb({ id }: { id: string }) {
  const { openMedia } = useUiStore()
  return (
    <button
      onClick={() => openMedia(mediaApi.getURL(id))}
      className="aspect-square rounded-lg overflow-hidden bg-bg-input"
    >
      <img
        src={mediaApi.getThumbnailURL(id, '200x200')}
        alt="media"
        className="w-full h-full object-cover"
        loading="lazy"
        onError={(e) => {
          const parent = e.currentTarget.parentElement as HTMLElement | null
          if (parent) parent.style.display = 'none'
        }}
      />
    </button>
  )
}
